import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateToken, GuestTokenPayload, sessionLogout } from '../../service/GuestAuthService';

export interface GuestAuthContextValue {
    guestAuth: GuestTokenPayload | null;
    setGuestAuth: (payload: GuestTokenPayload | null) => void;
    isLoggedIn: boolean;
    loading: boolean;
    isTokenValid: () => boolean;
    logout: () => void;
    sessionTimedOut: (tokenOverride?: string) => void;
    extendSession: () => Promise<boolean>;
    silentSignInInitialize: () => void;
}

const GuestAuthContext = createContext<GuestAuthContextValue | undefined>(undefined);

const THREE_MINUTES_MS = 3 * 60 * 1000;
const REFRESH_INTERVAL_MS = 60_000;
function decodeTokenExp(token: string): number | null {
    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const { exp } = JSON.parse(atob(base64));
        return typeof exp === 'number' ? exp : null;
    } catch {
        return null;
    }
}

export const GuestAuthProvider = ({ children }: { children: React.ReactNode }) => {
    const navigate = useNavigate();
    const [guestAuth, setGuestAuthState] = useState<GuestTokenPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const guestAuthRef = useRef(guestAuth);
    const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const sessionClosingRef = useRef(false);

    useEffect(() => {
        generateToken()
            .then((payload) => { setGuestAuthState(payload); })
            .catch(() => { })
            .finally(() => { setLoading(false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        guestAuthRef.current = guestAuth;
    }, [guestAuth]);

    const setGuestAuth = useCallback((payload: GuestTokenPayload | null) => {
        sessionClosingRef.current = false;
        setGuestAuthState(payload);
    }, []);

    const isLoggedIn = guestAuth !== null;

    const isTokenValid = useCallback((): boolean => {
        if (!guestAuth?.token) return false;
        const exp = decodeTokenExp(guestAuth.token);
        return exp !== null && exp * 1000 > Date.now();
    }, [guestAuth]);

    const clearRefreshTimer = useCallback(() => {
        if (refreshTimerRef.current) {
            clearInterval(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
    }, []);

    const logout = useCallback((): void => {
        sessionClosingRef.current = true;
        if (guestAuth?.token) {
            sessionLogout(guestAuth.token).catch(() => {});
        }
        clearRefreshTimer();
        setGuestAuthState(null);
        navigate('/');
    }, [navigate, guestAuth, clearRefreshTimer]);

    const sessionTimedOut = useCallback((tokenOverride?: string): void => {
        sessionClosingRef.current = true;
        const token = tokenOverride ?? guestAuthRef.current?.token;
        clearRefreshTimer();
        if (token) {
            sessionLogout(token).catch(() => {});
        }
        setGuestAuthState(null);
        window.location.replace(`${window.location.origin}${window.location.pathname}#/`);
    }, [clearRefreshTimer]);

    const extendSession = useCallback(async (): Promise<boolean> => {
        try {
            sessionClosingRef.current = false;
            const payload = await generateToken();
            if (sessionClosingRef.current) {
                return false;
            }
            setGuestAuthState(payload);
            return true;
        } catch {
            sessionTimedOut();
            return false;
        }
    }, [sessionTimedOut]);

    const silentSignInInitialize = useCallback((): void => {
        if (refreshTimerRef.current) {
            return;
        }
        refreshTimerRef.current = setInterval(async () => {
            const currentAuth = guestAuthRef.current;
            if (!currentAuth?.token) return;
            const exp = decodeTokenExp(currentAuth.token);
            if (exp === null) return;
            if (exp * 1000 - Date.now() >= THREE_MINUTES_MS) return;
            try {
                const payload = await generateToken();
                if (sessionClosingRef.current) {
                    return;
                }
                setGuestAuthState(payload);
            } catch {
                sessionTimedOut();
            }
        }, REFRESH_INTERVAL_MS);
    }, [sessionTimedOut]);

    const value: GuestAuthContextValue = {
        guestAuth,
        setGuestAuth,
        isLoggedIn,
        loading,
        isTokenValid,
        logout,
        sessionTimedOut,
        extendSession,
        silentSignInInitialize,
    };

    return <GuestAuthContext.Provider value={value}>{children}</GuestAuthContext.Provider>;
};

export const useGuestAuth = (): GuestAuthContextValue => {
    const context = useContext(GuestAuthContext);
    if (context === undefined) {
        throw new Error('useGuestAuth must be used within a GuestAuthProvider');
    }
    return context;
};
